import * as undoActions from 'owa-mail-undo';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import folderStore from 'owa-folders';
import getFolderData from 'owa-mail-actions/lib/getFolderData';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import isJunkEmailEnabledByAdmin from '../selectors/isJunkEmailEnabledByAdmin';
import {
    getUserPermissionForFolderId as mailStoreGetUserPermissionForFolderId,
    getUserPermissionForFolderIdWithErr as mailStoreGetUserPermissionForFolderIdWithErr,
} from 'owa-mail-store';
import { getFolderIdFromTableView } from 'owa-mail-search';
import { Permission, SourceWellKnownFolderType } from 'owa-folders';
import { getUserConfiguration } from 'owa-session-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isGroupTableQuery } from 'owa-group-utils';
import { isInboxRuleEnabledByAdmin } from 'owa-mail-rule-store';
import { isSnoozeScheduleEnabled } from 'owa-mail-list-store/lib/utils/snoozeUtils';
import isTranslatorEnabled from 'owa-addins-outlook-translate/lib/isTranslatorEnabled';
import { lazyGetRetentionPolicyTagListForGroupTableQuery } from 'owa-group-utils';
import { MenuItemType } from '../components/MenuItemType';
import { SearchScopeKind } from 'owa-search-service';
import { ShouldShowBehavior } from 'owa-filterable-menu';
import {
    getSelectedTableView,
    isItemPartOperation,
    TableQueryType,
    MailRowDataPropertyGetter,
    MailSortHelper,
    MailFolderTableQuery,
} from 'owa-mail-list-store';
import { shouldShowListView, shouldShowFolderPane } from 'owa-mail-layout';
import { shouldShowMenuItem } from '../components/Behaviors';
import {
    SelectionRestrictionType,
    ListRestrictionType,
    ReadingPaneRestrictionType,
    MailboxType,
    ListViewTypeRestrictionType,
    ItemPartSelectionRestrictionType,
} from '../components/Behaviors.types';
import {
    PRIMARY_DUMPSTER_DISTINGUISHED_ID,
    ARCHIVE_DUMPSTER_DISTINGUISHED_ID,
    isFolderOfTreeType,
} from 'owa-folders';
import { customRestriction } from '../components/behaviors/customRestriction';
import { folderRestriction } from '../components/behaviors/folderRestriction';
import { staticFolderSearchScopeRestriction } from '../components/behaviors/staticFolderSearchScopeRestriction';
import { searchScopeRestriction } from '../components/behaviors/searchScopeRestriction';
import { focusedViewFilterRestriction } from '../components/behaviors/focusedViewFilterRestriction';
import { listRestrictionType } from '../components/behaviors/listRestrictionType';
import { readingPaneRestrictionType } from '../components/behaviors/readingPaneRestrictionType';
import { listViewTypeRestrictionType } from '../components/behaviors/listViewTypeRestrictionType';
import { mailScenarioRestriction } from '../components/behaviors/mailScenarioRestriction';
import { tableQueryTypeRestriction } from '../components/behaviors/tableQueryTypeRestriction';
import { sortByRestriction } from '../components/behaviors/sortByRestriction';
import { itemPartSelectionRestrictionType } from '../components/behaviors/itemPartSelectionRestrictionType';
import { inboxClassificationFilterRestriction } from '../components/behaviors/inboxClassificationFilterRestriction';
import { default as mailboxRestriction } from '../components/behaviors/mailboxRestriction';
import { default as selectionRestriction } from '../components/behaviors/selectionRestriction';
import { default as notesFolderRestriction } from '../components/behaviors/notesFolderRestriction';
import InboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/store/schema/InboxClassificationFilter';

let shouldShowBehaviorMap: { [id: number]: ShouldShowBehavior };

/**
 * Get a map which maps a mail filterable menu item to its ShouldShowBehavior
 */
export default function getMailMenuItemShouldShowMap() {
    if (!shouldShowBehaviorMap) {
        initializeMap();
    }

    return shouldShowBehaviorMap;
}

/**
 * Initialize the ShouldShowBehaviors for mail menu items
 */
function initializeMap() {
    const inboxFolderId = 'inbox';
    const dumpsterFolderIds: string[] = [
        PRIMARY_DUMPSTER_DISTINGUISHED_ID,
        ARCHIVE_DUMPSTER_DISTINGUISHED_ID,
    ];
    const sentItemsFolderId = 'sentitems';
    const draftsFolderId = 'drafts';
    const deletedItemsFolderId = 'deleteditems';
    const junkFolderId = 'junkemail';
    const scheduledFolderId = 'scheduled';
    const clutterFolderId = 'clutter';
    const archiveFolderId = 'archive';
    const notesFolderId = 'notes';

    shouldShowBehaviorMap = {
        // Show the menu item if list view has single selection
        [MenuItemType.SingleSelection]: shouldShowMenuItem([
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
        ]),

        // Show overflow divider if
        // - in single selection
        // - not in recoverable items view
        [MenuItemType.OverflowDivider]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
        ]),

        // Show reply / reply all / forward in command bar if
        // - not in recoverable items view or drafts or junk folder
        // - there's a single selection
        // - it's not a group
        // - in message view
        [MenuItemType.ReplyReplyAllForwardCommandBar]: shouldShowMenuItem([
            mailboxRestriction([MailboxType.GroupMBX], true),
            folderRestriction(
                [draftsFolderId, junkFolderId, notesFolderId, ...dumpsterFolderIds],
                true
            ),
            listViewTypeRestrictionType(ListViewTypeRestrictionType.Message),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
        ]),

        // Show reply if
        // - not in recoverable items view
        // - there's a single selection
        // - it's not a group
        // - in message view
        [MenuItemType.Reply]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            mailboxRestriction([MailboxType.GroupMBX], true),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            listViewTypeRestrictionType(ListViewTypeRestrictionType.Message),
        ]),

        // Show reply all and forward if
        // - not in recoverable items view
        // - there's a single selection
        // - in message view
        [MenuItemType.ReplyAllForward]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            listViewTypeRestrictionType(ListViewTypeRestrictionType.Message),
        ]),

        // Show schedule menu item if
        // - user is in inbox or scheduled
        // - user is not in archive mbx, group mbx, shadow mbx, shared folders scenarios
        // - there's a mail list item selection
        [MenuItemType.Schedule]: shouldShowMenuItem([
            folderRestriction([inboxFolderId, scheduledFolderId]),
            mailboxRestriction(
                [
                    MailboxType.GroupMBX,
                    MailboxType.ArchiveMBX,
                    MailboxType.ShadowMBX,
                    MailboxType.SharedFolderMBX,
                ],
                true
            ),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
            staticFolderSearchScopeRestriction([]),
            customRestriction(() => {
                return isSnoozeScheduleEnabled();
            }),
        ]),

        // Show new message in right pane command bar
        // - user is not in dumpster folder (or search)
        // - when folder pane is collapsed
        [MenuItemType.NewMessage]: shouldShowMenuItem([
            folderRestriction(dumpsterFolderIds, true),
            staticFolderSearchScopeRestriction(dumpsterFolderIds, true),
            notesFolderRestriction(),
            customRestriction(() => {
                return !shouldShowFolderPane();
            }),
        ]),

        // Show new note in right pane command bar
        // - when folder pane is collapsed and we're in the notes folder
        [MenuItemType.NewNote]: shouldShowMenuItem([
            folderRestriction([notesFolderId]),
            customRestriction(() => {
                return isFeatureEnabled('notes-folder-view') && !shouldShowFolderPane();
            }),
        ]),

        // Show mark as read if
        // - not in recoverable items view
        // - list view has multiple selections
        // - or list view has single selection and selected row is unread
        [MenuItemType.MarkAsRead]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return (
                    // Show mark as read if
                    // 1) List view has single selection AND row is unread AND we are not acting on item parts
                    // 2) At least one selected item part is unread AND we are acting on item parts
                    isItemPartOperation() ||
                    (selectionRestriction([SelectionRestrictionType.SingleSelection])() &&
                        !isSingleSelectedRowRead()) ||
                    selectionRestriction([
                        SelectionRestrictionType.MultipleSelection,
                        SelectionRestrictionType.SelectAll,
                    ])()
                );
            }),
        ]),

        // Show mark as unread if not in recoverable items and list view has multiple selections
        // or list view has single selection and selected row is read
        [MenuItemType.MarkAsUnread]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return (
                    // Show mark as unread if
                    // 1) List view has single selection AND row is read AND we are not acting on item parts
                    // 2) At least one selected item part is read AND we are acting on item parts
                    isItemPartOperation() ||
                    (selectionRestriction([SelectionRestrictionType.SingleSelection])() &&
                        isSingleSelectedRowRead()) ||
                    selectionRestriction([
                        SelectionRestrictionType.MultipleSelection,
                        SelectionRestrictionType.SelectAll,
                    ])()
                );
            }),
        ]),

        // Show flag if
        // 1) List view has single selection AND row is un-flagged AND we are not acting on item parts
        // 2) List view has multiple selection
        // 3) At least one selected item part is un-flagged AND we are acting on item parts
        // 4) It is shared folder and user has edit permission for shared folder
        // Hide if
        // 1) Group mbx
        // 2) Notes folder and dumpster folder
        [MenuItemType.Flag]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            mailboxRestriction([MailboxType.GroupMBX], true),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return (
                    (isItemPartOperation() ||
                        (selectionRestriction([SelectionRestrictionType.SingleSelection])() &&
                            !isSingleSelectedRowFlagged()) ||
                        selectionRestriction([SelectionRestrictionType.MultipleSelection])()) &&
                    doesUserHaveSharedFolderPermissionFor(MenuItemType.Flag)
                );
            }),
        ]),

        // Show unflag if
        // - not in recoverable items view
        // - list view has multiple selections
        // - or list view has single selection and selected row is flagged
        [MenuItemType.Unflag]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            mailboxRestriction([MailboxType.GroupMBX], true),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return (
                    // Show unflag if
                    // 1) List view has single selection AND row is flagged AND we are not acting on item parts
                    // 2) At least one selected item part is flagged AND we are acting on item parts
                    // 3) It is a shared folder and user has edit all permission
                    (isItemPartOperation() ||
                        (selectionRestriction([SelectionRestrictionType.SingleSelection])() &&
                            isSingleSelectedRowFlagged()) ||
                        selectionRestriction([SelectionRestrictionType.MultipleSelection])()) &&
                    doesUserHaveSharedFolderPermissionFor(MenuItemType.Unflag)
                );
            }),
        ]),

        // Show categories if
        // - not in recoverable items view
        // - list view has selection
        // - not in groups
        // - user is in a shared folder and has edit all permission
        [MenuItemType.Categories]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            mailboxRestriction([MailboxType.GroupMBX, MailboxType.ShadowMBX], true),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return doesUserHaveSharedFolderPermissionFor(MenuItemType.Categories);
            }),
        ]),

        // Show pin menu items
        // - in all folders except the ones in the restriction list
        // - and its not a archive mbx, group mbx
        // - (and list view has single selection and it's unpinned
        // ----- or list view has multiple selections)
        [MenuItemType.Pin]: shouldShowMenuItem([
            folderRestriction(
                [
                    draftsFolderId,
                    sentItemsFolderId,
                    clutterFolderId,
                    junkFolderId,
                    deletedItemsFolderId,
                    scheduledFolderId,
                    archiveFolderId,
                    notesFolderId,
                    ...dumpsterFolderIds,
                ],
                true
            ),
            mailboxRestriction(
                [MailboxType.GroupMBX, MailboxType.ArchiveMBX, MailboxType.SharedFolderMBX],
                true
            ),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            sortByRestriction([MailSortHelper.getSortBySupportingPin()]),
            searchScopeRestriction(
                [SearchScopeKind.ArchiveMailbox, SearchScopeKind.SharedFolders],
                true
            ),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return (
                    selectionRestriction([SelectionRestrictionType.MultipleSelection])() ||
                    (selectionRestriction([SelectionRestrictionType.SingleSelection])() &&
                        !isSingleSelectedRowPinned())
                );
            }),
        ]),

        // Show pin menu items
        // - in all folders except the ones in the restriction list
        // - and not in archive mbx or group mbx
        // - and list view has single selection and it's pinned
        // ----- or list view has multiple selections
        [MenuItemType.Unpin]: shouldShowMenuItem([
            folderRestriction(
                [
                    draftsFolderId,
                    sentItemsFolderId,
                    clutterFolderId,
                    junkFolderId,
                    deletedItemsFolderId,
                    archiveFolderId,
                    notesFolderId,
                    ...dumpsterFolderIds,
                ],
                true
            ),
            mailboxRestriction(
                [MailboxType.ArchiveMBX, MailboxType.GroupMBX, MailboxType.SharedFolderMBX],
                true
            ),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            sortByRestriction([MailSortHelper.getSortBySupportingPin()]),
            searchScopeRestriction(
                [SearchScopeKind.ArchiveMailbox, SearchScopeKind.SharedFolders],
                true
            ),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return (
                    selectionRestriction([SelectionRestrictionType.MultipleSelection])() ||
                    (selectionRestriction([SelectionRestrictionType.SingleSelection])() &&
                        isSingleSelectedRowPinned())
                );
            }),
        ]),

        // Show delete
        // - when list view has selection
        // - If it is shared folder and folder has delete all permission
        // - In 'Notes' folder unless 'notes-folder-view' flight is enabled
        [MenuItemType.Delete]: shouldShowMenuItem([
            selectionRestriction([
                SelectionRestrictionType.HasSelection,
                SelectionRestrictionType.SelectAll,
            ]),
            notesFolderRestriction(),
            customRestriction(() => {
                return doesUserHaveSharedFolderPermissionFor(MenuItemType.Delete);
            }),
        ]),

        // Show archive
        // - in folders under msgfolderroot except archive, deleted items, drafts, junk folder, and recoverable items
        // - and when list view has selection
        // - or if list view has selection in search
        [MenuItemType.Archive]: shouldShowMenuItem([
            folderRestriction(
                [
                    archiveFolderId,
                    deletedItemsFolderId,
                    draftsFolderId,
                    junkFolderId,
                    notesFolderId,
                    ...dumpsterFolderIds,
                ],
                true
            ),
            mailboxRestriction(
                [MailboxType.GroupMBX, MailboxType.ArchiveMBX, MailboxType.SharedFolderMBX],
                true
            ),
            tableQueryTypeRestriction([TableQueryType.Folder, TableQueryType.Search]),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
            searchScopeRestriction(
                [SearchScopeKind.ArchiveMailbox, SearchScopeKind.SharedFolders],
                true
            ),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
        ]),

        // Show mark as junk
        // - when list view has selection
        // - and in folders under msgfolderroot except sent items, junk email, drafts, clutter, scheduled, and recoverable items
        // - or when list view has selection in search and the search refiner is not junkemail
        [MenuItemType.MarkAsJunk]: shouldShowMenuItem([
            folderRestriction(
                [
                    draftsFolderId,
                    sentItemsFolderId,
                    junkFolderId,
                    clutterFolderId,
                    scheduledFolderId,
                    notesFolderId,
                    ...dumpsterFolderIds,
                ],
                true
            ),
            mailboxRestriction(
                [
                    MailboxType.ArchiveMBX,
                    MailboxType.GroupMBX,
                    MailboxType.ShadowMBX,
                    MailboxType.SharedFolderMBX,
                ],
                true
            ),
            mailScenarioRestriction(['persona', 'category'], true),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
            searchScopeRestriction(
                [SearchScopeKind.ArchiveMailbox, SearchScopeKind.SharedFolders],
                true
            ),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return isJunkEmailEnabledByAdmin();
            }),
        ]),

        // Show mark as not junk
        // - in junk folder
        // - and when list view has selection
        // - or when list view has selection in search and the search refiner is junkemail
        // (But ALWAYS hide when in a Group/Shadow/Archive Mailbox)
        [MenuItemType.MarkAsNotJunk]: shouldShowMenuItem([
            folderRestriction([junkFolderId]),
            mailboxRestriction(
                [
                    MailboxType.ArchiveMBX,
                    MailboxType.GroupMBX,
                    MailboxType.ShadowMBX,
                    MailboxType.SharedFolderMBX,
                ],
                true
            ),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
            searchScopeRestriction(
                [SearchScopeKind.ArchiveMailbox, SearchScopeKind.SharedFolders],
                true
            ),
            staticFolderSearchScopeRestriction([junkFolderId]),
            customRestriction(() => {
                return isJunkEmailEnabledByAdmin();
            }),
        ]),

        // Show ignore
        // - when list view has selection
        // - and not in Sent Items, drafts, deleted items, junk, or dumpster
        // (But ALWAYS hide when in a ShadowMailbox, Group mbx, Archive mbx)
        [MenuItemType.Ignore]: shouldShowMenuItem([
            folderRestriction(
                [
                    sentItemsFolderId,
                    draftsFolderId,
                    deletedItemsFolderId,
                    junkFolderId,
                    notesFolderId,
                    ...dumpsterFolderIds,
                ],
                true
            ),
            mailboxRestriction(
                [
                    MailboxType.ArchiveMBX,
                    MailboxType.GroupMBX,
                    MailboxType.ShadowMBX,
                    MailboxType.SharedFolderMBX,
                ],
                true
            ),
            tableQueryTypeRestriction([TableQueryType.Folder, TableQueryType.Search]),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            searchScopeRestriction(
                [SearchScopeKind.ArchiveMailbox, SearchScopeKind.SharedFolders],
                true
            ),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
            staticFolderSearchScopeRestriction([notesFolderId]),
        ]),

        // Show report abuse
        // - when list view has single selection
        [MenuItemType.ReportAbuse]: shouldShowMenuItem([
            folderRestriction([notesFolderId], true),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
            customRestriction(() => {
                return (
                    isFeatureEnabled('tri-reportAbuse') &&
                    ((mailboxRestriction([MailboxType.GroupMBX])() && isConsumer()) ||
                        mailboxRestriction([MailboxType.PrimaryMBX])())
                );
            }),
        ]),

        // Show stop ignoring
        // - when list view has selection
        // - only in deleted items
        // (But ALWAYS hide when in a ShadowMailbox, Group mbx, Archive mbx)
        [MenuItemType.StopIgnoring]: shouldShowMenuItem([
            folderRestriction([deletedItemsFolderId]),
            mailboxRestriction([MailboxType.PrimaryMBX]),
            tableQueryTypeRestriction([TableQueryType.Search], true),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
        ]),

        // Show block sender
        // - not in search(because search doesn't return lastSender property)
        // - when list view has selection with a LastSender property on the item
        // - and in folders under msgfolderroot except sent items, junk email, drafts, clutter, scheduled and recoverable items
        // - and when list view has single selection and it has valid smtp address
        [MenuItemType.Block]: shouldShowMenuItem([
            folderRestriction(
                [
                    draftsFolderId,
                    sentItemsFolderId,
                    clutterFolderId,
                    scheduledFolderId,
                    notesFolderId,
                    ...dumpsterFolderIds,
                ],
                true
            ),
            mailboxRestriction([MailboxType.GroupMBX, MailboxType.ShadowMBX], true),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            customRestriction(() => {
                return (
                    (selectionRestriction([SelectionRestrictionType.SingleSelection])() &&
                        doesSingleSelectionHasSmtp()) ||
                    selectionRestriction([SelectionRestrictionType.MultipleSelection])()
                );
            }),
        ]),

        // Show sweep
        // - in folders except sent, drafts, deleteditems, junk email, and recoverable items
        // - and the mbx is not archive, group
        // - and when user is not in search mode
        // - and when user is not in a category folder
        // - and when list view has
        // ------ multiple selections,
        // ------ OR single selection and it has smtp address
        [MenuItemType.Sweep]: shouldShowMenuItem([
            folderRestriction(
                [
                    sentItemsFolderId,
                    draftsFolderId,
                    deletedItemsFolderId,
                    junkFolderId,
                    notesFolderId,
                    ...dumpsterFolderIds,
                ],
                true
            ),
            mailboxRestriction(
                [MailboxType.ArchiveMBX, MailboxType.GroupMBX, MailboxType.SharedFolderMBX],
                true
            ),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            tableQueryTypeRestriction([TableQueryType.Search], true),
            mailScenarioRestriction(['category'], true),
            customRestriction(() => {
                return (
                    (selectionRestriction([SelectionRestrictionType.SingleSelection])() &&
                        doesSingleSelectionHasSmtp()) ||
                    selectionRestriction([SelectionRestrictionType.MultipleSelection])()
                );
            }),
        ]),

        // Show empty folder menu item if
        // - list view has items and has no selection
        // - not in groups
        // - if in search or favorite node where selected folder id is null
        // - not in notes folder
        // - OR
        // - when in Inbox and FocusedViewFilter is Other
        // - OR
        // - in non-Inbox/Focused pivots (Inbox plus tabs)
        [MenuItemType.EmptyFolder]: shouldShowMenuItem([
            [
                folderRestriction([inboxFolderId], true),
                tableQueryTypeRestriction([TableQueryType.Folder, TableQueryType.Search]),
                mailboxRestriction([MailboxType.GroupMBX], true),
                listRestrictionType(ListRestrictionType.WithItems),
                selectionRestriction([SelectionRestrictionType.NoSelection]),
                notesFolderRestriction(),
                customRestriction((selectedFolderDistinguishedId: string) => {
                    // This custom restriction is only valid for the folder table types
                    // In this particular case we check for the distinguished folder id.
                    const isDumpsterOrNotArchiveShadowMailboxFolder =
                        isMailFolderTableShown() &&
                        selectedFolderDistinguishedId &&
                        (dumpsterFolderIds.indexOf(selectedFolderDistinguishedId) > -1 ||
                            isNotArchiveInShadowMailbox(selectedFolderDistinguishedId));
                    return (
                        isDumpsterOrNotArchiveShadowMailboxFolder ||
                        doesUserHaveSharedFolderPermissionFor(MenuItemType.Delete)
                    );
                }),
            ],
            [
                inboxClassificationFilterRestriction([
                    InboxClassificationFilter.Promotions,
                    InboxClassificationFilter.Social,
                    InboxClassificationFilter.Newsletters,
                    InboxClassificationFilter.Other,
                ]),
                listRestrictionType(ListRestrictionType.WithItems),
                selectionRestriction([SelectionRestrictionType.NoSelection]),
                mailboxRestriction([MailboxType.GroupMBX], true),
            ],
            [
                folderRestriction([inboxFolderId], false),
                focusedViewFilterRestriction([FocusedViewFilter.Other]),
                listRestrictionType(ListRestrictionType.WithItems),
                selectionRestriction([SelectionRestrictionType.NoSelection]),
                mailboxRestriction([MailboxType.GroupMBX], true),
            ],
        ]),

        // Show mark all as read menu item
        // - not in recoverable items view
        // - and list view has unread item
        // - and list view has no selection
        [MenuItemType.MarkAllAsRead]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            listRestrictionType(ListRestrictionType.WithItems),
            selectionRestriction([SelectionRestrictionType.NoSelection]),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
        ]),

        // Show restore if
        // - list view has selections
        // - and in deleteditems or dumpster folder
        // - and in search when the search scope is deleteditems for dumpster folder
        [MenuItemType.Restore]: shouldShowMenuItem([
            selectionRestriction([SelectionRestrictionType.HasSelection]),
            folderRestriction([deletedItemsFolderId, ...dumpsterFolderIds]),
            mailboxRestriction([MailboxType.GroupMBX], true),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            staticFolderSearchScopeRestriction([...dumpsterFolderIds, deletedItemsFolderId]),
        ]),

        // Show undo if
        // - not in recoverable items view
        // - not in archive mbx, group mbx
        // - In folder list view
        // - When list pane is showing
        // - list view has items OR undo stack is not empty
        // - in 'Notes' folder unless 'notes-folder-view' flight is enabled
        [MenuItemType.Undo]: shouldShowMenuItem([
            folderRestriction(dumpsterFolderIds, true),
            mailboxRestriction(
                [MailboxType.ArchiveMBX, MailboxType.GroupMBX, MailboxType.SharedFolderMBX],
                true
            ),
            tableQueryTypeRestriction([TableQueryType.Folder]),
            notesFolderRestriction(),
            customRestriction(() => {
                return (
                    shouldShowListView() &&
                    (undoActions.hasUndoableAction() ||
                        listRestrictionType(ListRestrictionType.WithItems)())
                );
            }),
        ]),

        // Show move to if
        // - Not in draft folder or recoverable items view
        // - Not in groups mbx
        // - list view has selection
        // - Shared folder has delete all permission
        [MenuItemType.Move]: shouldShowMenuItem([
            folderRestriction([draftsFolderId, notesFolderId, ...dumpsterFolderIds], true),
            mailboxRestriction([MailboxType.GroupMBX], true),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return doesUserHaveSharedFolderPermissionFor(MenuItemType.Move);
            }),
        ]),

        // Show copy to if
        // - Not in draft folder or recoverable items view
        // - Not in groups mbx
        // - list view has selection
        // - Shared folder has delete all permission
        [MenuItemType.Copy]: shouldShowMenuItem([
            folderRestriction([draftsFolderId, notesFolderId, ...dumpsterFolderIds], true),
            mailboxRestriction([MailboxType.GroupMBX], true),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return doesUserHaveSharedFolderPermissionFor(MenuItemType.Copy);
            }),
        ]),

        // Show inbox rule if
        // - not in sent items or drafts folders (until 'sent to' rule field is supported) or recoverable items view, and not a group
        // - and list view has single selection
        [MenuItemType.InboxRule]: shouldShowMenuItem([
            folderRestriction(
                [sentItemsFolderId, draftsFolderId, notesFolderId, ...dumpsterFolderIds],
                true
            ),
            mailboxRestriction(
                [
                    MailboxType.ArchiveMBX,
                    MailboxType.GroupMBX,
                    MailboxType.SharedFolderMBX,
                    MailboxType.ShadowMBX,
                ],
                true
            ),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            searchScopeRestriction(
                [SearchScopeKind.ArchiveMailbox, SearchScopeKind.SharedFolders],
                true
            ),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
            staticFolderSearchScopeRestriction(
                [sentItemsFolderId, notesFolderId, draftsFolderId],
                true
            ),
            customRestriction(() => {
                return isInboxRuleEnabledByAdmin() && doesSingleSelectionHasSmtp();
            }),
        ]),

        // Show move to Focused inbox menu item
        // - when is in Other inbox
        // - and list view has items
        // - and list view has selection
        // - and when not in search
        [MenuItemType.MoveToFocusedInbox]: shouldShowMenuItem([
            folderRestriction([inboxFolderId]),
            focusedViewFilterRestriction([FocusedViewFilter.Other]),
            listRestrictionType(ListRestrictionType.WithItems),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            tableQueryTypeRestriction([TableQueryType.Search], true),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
        ]),

        // Show move to Other inbox menu item
        // - when in Focused inbox
        // - and list view has items
        // - and list view has selection
        // - and when not in search
        [MenuItemType.MoveToOtherInbox]: shouldShowMenuItem([
            folderRestriction([inboxFolderId]),
            focusedViewFilterRestriction([FocusedViewFilter.Focused]),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            listRestrictionType(ListRestrictionType.WithItems),
            tableQueryTypeRestriction([TableQueryType.Search], true),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
        ]),

        // Show always move to Focused inbox menu item
        // - when not in search
        // - list view has items
        // - and list view has single selection
        // - is in Other inbox
        [MenuItemType.AlwaysMoveToFocusedInbox]: shouldShowMenuItem([
            folderRestriction([inboxFolderId]),
            focusedViewFilterRestriction([FocusedViewFilter.Other]),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            listRestrictionType(ListRestrictionType.WithItems),
            tableQueryTypeRestriction([TableQueryType.Search], true),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
        ]),

        // Show always move to Other inbox menu item
        // - is in Focused inbox
        // - and list view has items
        // - and list view has single selection
        // - and when not in search
        [MenuItemType.AlwaysMoveToOtherInbox]: shouldShowMenuItem([
            folderRestriction([inboxFolderId]),
            focusedViewFilterRestriction([FocusedViewFilter.Focused]),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            listRestrictionType(ListRestrictionType.WithItems),
            tableQueryTypeRestriction([TableQueryType.Search], true),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
        ]),

        [MenuItemType.UpNext]: shouldShowMenuItem([
            mailboxRestriction([MailboxType.GroupMBX], true),
        ]),

        [MenuItemType.ClientOptOut]: shouldShowMenuItem([
            mailboxRestriction([MailboxType.GroupMBX], true),
            customRestriction(() => {
                const userConfiguration = getUserConfiguration();
                const userOptions = userConfiguration.UserOptions;
                return (
                    shouldShowListView() &&
                    userOptions.ReactOptinSettings &&
                    !userOptions.IsPermanentlyOptedIn &&
                    !userOptions.ReactOptinSettings.MailGraduatedFromBeta &&
                    (userConfiguration.PolicySettings.OutlookBetaToggleEnabled !== undefined
                        ? userConfiguration.PolicySettings.OutlookBetaToggleEnabled
                        : true)
                );
            }),
        ]),

        // Show Print menu
        // - in all folders except draft folder and recoverable items view
        // - and list view has single selection
        // - in 'Notes' folder unless 'notes-folder-view' flight is enabled
        [MenuItemType.Print]: shouldShowMenuItem([
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            folderRestriction([draftsFolderId, ...dumpsterFolderIds], true),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
            staticFolderSearchScopeRestriction(dumpsterFolderIds, true),
            notesFolderRestriction(),
        ]),

        // Show "Translate" menu
        // - in all folders except draft folder and recoverable items view
        // - and list view has single selection
        [MenuItemType.Translate]: shouldShowMenuItem([
            folderRestriction([draftsFolderId, notesFolderId, ...dumpsterFolderIds], true),
            mailboxRestriction([MailboxType.GroupMBX], true),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
            readingPaneRestrictionType(ReadingPaneRestrictionType.Right),
            staticFolderSearchScopeRestriction([notesFolderId], true),
            customRestriction(() => {
                return isFeatureEnabled('gsx-translator-translateMessage') && isTranslatorEnabled();
            }),
        ]),

        // Show "Show in immersive reader" menu
        // - list view has single selection
        // - not in recoverable items view
        [MenuItemType.ShowInImmersiveReader]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            selectionRestriction([SelectionRestrictionType.SingleSelection]),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                return isFeatureEnabled('rp-immersiveReader');
            }),
        ]),

        // Show "Open in new tab" menu
        // - list view has single selection
        // - not in recoverable items view
        [MenuItemType.OpenInNewTab]: shouldShowMenuItem([
            folderRestriction(dumpsterFolderIds, true),
            itemPartSelectionRestrictionType(ItemPartSelectionRestrictionType.NoSelection),
            selectionRestriction([
                SelectionRestrictionType.SingleSelection,
                SelectionRestrictionType.MultipleSelection,
            ]),
            staticFolderSearchScopeRestriction(dumpsterFolderIds, true),
        ]),

        // Show assign policy if
        // - not in recoverable items view
        // - it's not in shared folder
        // - list view has selection
        [MenuItemType.AssignPolicy]: shouldShowMenuItem([
            folderRestriction([notesFolderId, ...dumpsterFolderIds], true),
            selectionRestriction([SelectionRestrictionType.HasSelection]),
            staticFolderSearchScopeRestriction([notesFolderId, ...dumpsterFolderIds], true),
            customRestriction(() => {
                if (isConsumer()) {
                    return false;
                }

                const tableView = getSelectedTableView();
                if (tableView && isGroupTableQuery(tableView.tableQuery)) {
                    const getRetentionPolicyTagListForGroupTableQuery = lazyGetRetentionPolicyTagListForGroupTableQuery.tryImportForRender();
                    const retentionTagsList = getRetentionPolicyTagListForGroupTableQuery
                        ? getRetentionPolicyTagListForGroupTableQuery(tableView.tableQuery)
                        : [];

                    return retentionTagsList && retentionTagsList.length > 0;
                }

                return mailboxRestriction(
                    [MailboxType.SharedFolderMBX, MailboxType.ShadowMBX],
                    true
                )();
            }),
        ]),

        // Show "Find email messages from sender" menu
        [MenuItemType.FindEmailFromSender]: shouldShowMenuItem([
            folderRestriction([...dumpsterFolderIds, sentItemsFolderId, draftsFolderId], true),
            listViewTypeRestrictionType(ListViewTypeRestrictionType.Message),
            customRestriction(() => {
                if (!isFeatureEnabled('sea-findFromSender')) {
                    return false;
                }

                return (
                    selectionRestriction([SelectionRestrictionType.SingleSelection])() &&
                    doesSingleSelectionHasSmtp()
                );
            }),
        ]),
    };
}

/**
 * Returns true if we are currently showing Folder type table for mail scenario
 */
function isMailFolderTableShown(): boolean {
    const tableView = getSelectedTableView();
    return (
        tableView.tableQuery.type == TableQueryType.Folder &&
        (tableView.tableQuery as MailFolderTableQuery).scenarioType == 'mail'
    );
}

/**
 * Returns a flag indicating whether the current selected folder is not a "Archive"
 * folder for a shadow mail box
 */
function isNotArchiveInShadowMailbox(selectedFolderDistinguishedId: string): boolean {
    const selectedDistinguishedFolder = folderStore.folderTable.get(
        folderNameToId(selectedFolderDistinguishedId)
    );

    // if both selectedDistinguishedFolder and sourceWellKnownFolderType are present
    // return true if the sourceWellKnownFolderType is not of type Archive.
    if (selectedDistinguishedFolder && selectedDistinguishedFolder.sourceWellKnownFolderType) {
        return (
            selectedDistinguishedFolder.sourceWellKnownFolderType !==
            SourceWellKnownFolderType.Archive
        );
    }

    // else return true
    return true;
}

/**
 * Returns true if list view has single selected row and it is read
 */
function isSingleSelectedRowRead() {
    const singleSelectedRowKey = getSingleSelectedRowKey();
    if (!singleSelectedRowKey) {
        // False when there is no selection or multiple selection
        return false;
    }

    const unreadCount = MailRowDataPropertyGetter.getUnreadCount(
        singleSelectedRowKey,
        getSelectedTableView()
    );

    return unreadCount === 0;
}

/**
 * Returns true if list view has single selected row and it is flagged
 */
function isSingleSelectedRowFlagged() {
    const singleSelectedRowKey = getSingleSelectedRowKey();
    if (!singleSelectedRowKey) {
        // False when there is no selection or multiple selection
        return false;
    }

    const flagStatus = MailRowDataPropertyGetter.getFlagStatus(
        singleSelectedRowKey,
        getSelectedTableView()
    );

    return flagStatus == 'Flagged';
}

/**
 * Returns true if list view has single selected row and it is pinned
 */
function isSingleSelectedRowPinned(): boolean {
    const singleSelectedRowKey = getSingleSelectedRowKey();
    if (!singleSelectedRowKey) {
        // False when there is no selection or multiple selection
        return false;
    }

    return MailRowDataPropertyGetter.getIsPinned(singleSelectedRowKey, getSelectedTableView());
}

/**
 * Returns true if single selected row data has smtp address
 */
function doesSingleSelectionHasSmtp(): boolean {
    const singleSelectedRowKey = getSingleSelectedRowKey();
    if (!singleSelectedRowKey) {
        // Hide 'Block' when there is no selection or multiple selection
        return false;
    }

    return !!MailRowDataPropertyGetter.getLastSenderSMTP(
        singleSelectedRowKey,
        getSelectedTableView()
    );
}

/**
 * Returns the single selected mail list row id in list view, null if otherwise
 */
function getSingleSelectedRowKey(): string {
    const selectedRowKeys = [...getSelectedTableView().selectedRowKeys.keys()];
    return selectedRowKeys.length == 1 ? selectedRowKeys[0] : null;
}

/**
 * Function return user assigned permission for the given folderId and user email address
 * If no permissions are present for user then return undefined
 * If there is no value in store for PermissionSet or Permissions then let the function throw error implicitly
 * @param folderId - folder for which the permissions will be calculated
 * @param userEmailAddress - user email for which permissions will be filtered
 */
function getUserPermissionForFolderIdWithErr(
    folderId: string,
    userEmailAddress: string
): Permission {
    return mailStoreGetUserPermissionForFolderIdWithErr(folderId, userEmailAddress);
}

/**
 * This function is wrapper over getUserPermissionForFolderIdWithErr function that can handle error.
 * In case of error this function will return null to denote that there are no permissions.
 * @param folderId - folder for which the permissions will be calculated
 * @param userEmailAddress - user email for which permissions will be filtered
 */
export function getUserPermissionForFolderId(
    folderId: string,
    userEmailAddress: string
): Permission {
    return mailStoreGetUserPermissionForFolderId(folderId, userEmailAddress);
}

/**
 * Function checks if the user is given shared folder permission to delete, move or flag items
 * If permissions are present but they are empty then assume user has all permissions
 * If Permissions are not present in store then throw custom error
 * @param actionType - Menu item type for which permission will be calculated
 * @param externalFolderId - Folder id provided externally to this function for which permissions will be checked
 */
export function doesUserHaveSharedFolderPermissionForWithError(
    actionType: MenuItemType,
    externalFolderId?: string
): boolean {
    // If folder Id is not provided in argument then get the selected folder node
    const folderId = externalFolderId || getFolderIdFromTableView(getSelectedTableView());
    const isSharedFolder = isFolderOfTreeType(folderId, 'sharedFolderTree');

    // If not shared folder show all triage options
    if (!isSharedFolder) {
        return true;
    }
    const userEmail = getUserConfiguration().SessionSettings.UserEmailAddress;

    // Try getting permissions by calling getUserPermissionForFolderIdWithErr.
    // If there is error then call getFolderData to get this shared folder's permissions.
    // And throw error for the consumer of this function indicating that permissions are currently not present.
    try {
        const assignedFolderPermission: Permission = getUserPermissionForFolderIdWithErr(
            folderId,
            userEmail
        );
        return doesUserHaveActionPermission(actionType, assignedFolderPermission);
    } catch (e) {
        getFolderData(folderId);
        throw new Error(
            'Move-To Error: Permissions for destination folder have not been fetched yet'
        );
    }
}

/**
 * Function checks if the user has correct permissions to do certain triage actions
 * @param actionType - Menu item type for which permission will be calculated
 * @param assignedFolderPermission - the folder permission object
 */
export function doesUserHaveActionPermission(
    actionType: MenuItemType,
    assignedFolderPermission: Permission
): boolean {
    // If permission object is null it means all permissions assigned
    if (!assignedFolderPermission) {
        return true;
    }

    switch (actionType) {
        // Show Move to and Delete options if
        // - User has delete all permission
        case MenuItemType.Move:
        case MenuItemType.Copy:
        case MenuItemType.Delete:
            return assignedFolderPermission.DeleteItems === 'All';

        // Show flag and categories options if
        // - User has edit all permission
        case MenuItemType.Flag:
        case MenuItemType.Unflag:
        case MenuItemType.Categories:
            return assignedFolderPermission.EditItems === 'All';
    }

    return false;
}

/**
 * This function is a wrapper over doesUserHaveSharedFolderPermissionForWithError
 * This function assumes that if permissions data is not fetched yet then automatically permission is provided
 * @param actionType - Menu item type for which permission will be calculated
 * @param folderId - Folder id for which we want to know if permission is present or not
 */
export function doesUserHaveSharedFolderPermissionFor(
    actionType: MenuItemType,
    folderId?: string
): boolean {
    try {
        return doesUserHaveSharedFolderPermissionForWithError(actionType, folderId);
    } catch (e) {
        return true;
    }
}
